import { ParticipantRef } from "@/api/types";

// Handle format mirrors the backend: starts alphanumeric, then alphanumerics
// or dashes. We match an `@`-prefixed token in the chat input.
const MENTION_RE = /@([a-z0-9][a-z0-9-]{0,29})/gi;

/** Unique, lowercased handles mentioned with `@` in the given text, in order. */
export function extractHandles(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const m of text.matchAll(MENTION_RE)) {
    const handle = m[1].toLowerCase();
    if (!seen.has(handle)) {
      seen.add(handle);
      out.push(handle);
    }
  }
  return out;
}

/** Participant refs for a message; all required in the single-peer path. */
export function extractParticipants(text: string): ParticipantRef[] {
  return extractHandles(text).map((handle) => ({ handle, required: true }));
}

/** The `@`-token the caret is currently inside, for autocomplete. */
export interface ActiveMention {
  /** Index of the `@` in `value`. */
  start: number;
  /** Text typed after the `@` up to the caret (may be empty). */
  query: string;
}

// Handle chars allowed after `@` while typing (superset of the final format;
// the backend validates). Used to decide whether the caret is still in a token.
const HANDLE_CHAR = /[a-zA-Z0-9-]/;

/**
 * If the caret sits inside an `@mention` token, return where it starts and the
 * partial handle typed so far; otherwise null. A mention only counts when the
 * `@` is at the start of the text or preceded by whitespace, so emails and
 * mid-word `@` don't trigger the dropdown.
 */
export function activeMention(value: string, caret: number): ActiveMention | null {
  for (let i = caret - 1; i >= 0; i--) {
    const ch = value[i];
    if (ch === "@") {
      const prev = i > 0 ? value[i - 1] : "";
      if (i === 0 || /\s/.test(prev)) {
        return { start: i, query: value.slice(i + 1, caret) };
      }
      return null;
    }
    if (!HANDLE_CHAR.test(ch)) return null;
  }
  return null;
}
